import {createStyles, Flex} from '@mantine/core';
import type {Dispatch, SetStateAction} from 'react';
import React from 'react';

import {trackEventWithModule} from 'analytics/Analytics';
import {UPDATE_FLOWCHART_MODAL_TYPE} from 'constants/analytics-products/WorkflowAnalyticsConstants';
import {
  ActionMethod,
  ActionOrigin,
  ANALYTICS_MODULE,
  COMMON_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {FlowchartPaneWrapper} from 'processes/modals/CreateProcessGraphBuilderPane';
import ExecutionFlowchartPane from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/execution/ExecutionFlowchartPane';
import type {ExecutionFlowchartReadinessSnapshot} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/execution/ExecutionFlowchartPaneTypes';
import {FlowchartConfigWithMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/flowchartConfigAutosaveManagerCallbacks';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

const COMPONENT_NAME = 'ImprovedCreateProcessFlowchartPane';

const useImprovedCreateProcessFlowchartPaneStyles = createStyles(() => ({
  viewModeFlowchart: {
    height: 'calc(100% - 40px)',
  },
  editModeFlowchart: {
    flex: 1,
    minHeight: 0,
  },
}));

export interface ImprovedCreateProcessFlowchartPaneProps {
  flowchartConfigId: string;
  isEditing: boolean;
  onEditStatusChange?: (isEditing: boolean) => void;
  draftState: FlowchartConfigWithMetadata | null;
  setDraftState: Dispatch<SetStateAction<FlowchartConfigWithMetadata | null>>;
  readinessSnapshot: ExecutionFlowchartReadinessSnapshot;
  onFlowchartEditBlockingErrorsChange?: (hasBlocking: boolean) => void;
  eventGroupId: string;
  taskSchemaApiId: string;
  isFlowchartModifiableOnCreation?: boolean;
}

/**
 * Create-process modal body: view vs edit layout around {@link ExecutionFlowchartPane} (ACM mode).
 * Parent owns draft state and passes `readinessSnapshot` from {@link useExecutionFlowchartDraftStateForConfig}.
 */
export default function ImprovedCreateProcessFlowchartPane(
  props: ImprovedCreateProcessFlowchartPaneProps
): JSX.Element {
  const {classes} = useImprovedCreateProcessFlowchartPaneStyles();
  const {isEditing, onEditStatusChange, onFlowchartEditBlockingErrorsChange} = props;
  const isFlowchartModifiableOnCreation = props.isFlowchartModifiableOnCreation ?? true;

  const commonFlowchartPaneProps = {
    flowchartConfigId: props.flowchartConfigId,
    readinessSnapshot: props.readinessSnapshot,
    draftState: props.draftState,
    setDraftState: props.setDraftState,
    onBlockingErrorsChange: onFlowchartEditBlockingErrorsChange,
  };

  const flowchartPane = isEditing ? (
    <ExecutionFlowchartPane {...commonFlowchartPaneProps} editorMode={FlowchartEditorMode.ACM_EDITOR} />
  ) : (
    <ExecutionFlowchartPane {...commonFlowchartPaneProps} editorMode={FlowchartEditorMode.EXECUTION_VIEWER} />
  );

  function getViewModeButtons(): JSX.Element | null {
    if (!isFlowchartModifiableOnCreation) {
      return null;
    }
    return (
      <Button
        text="Edit"
        icon="edit"
        onClick={() => {
          trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
            event_group_id: props.eventGroupId,
            action_origin: ActionOrigin.WORKFLOWS_TASK_GROUP_CREATION_MODAL_FLOWCHART_PANE,
            action_method: ActionMethod.AT_CREATION_FLOWCHART_MODIFICATION,
            task_schema_api_id: props.taskSchemaApiId,
            modal_type: UPDATE_FLOWCHART_MODAL_TYPE,
          });
          onEditStatusChange?.(true);
        }}
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.MINI}
        dataTestAttrs={{component: COMPONENT_NAME, element: 'editButton'}}
      />
    );
  }

  return (
    <Flex
      direction="column"
      h="100%"
      {...getTestProps({component: COMPONENT_NAME, element: 'flowchartPane'})}
    >
      {isEditing ? (
        <Flex direction="column" className={classes.editModeFlowchart}>
          {flowchartPane}
        </Flex>
      ) : (
        <FlowchartPaneWrapper getButtons={getViewModeButtons}>
          <div className={classes.viewModeFlowchart}>{flowchartPane}</div>
        </FlowchartPaneWrapper>
      )}
    </Flex>
  );
}
